import React, { useState, useEffect } from 'react';
import { ExternalLink, ChevronRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../lib/i18n';

interface CustomerPortalPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CustomerPortalPanel({ isOpen, onClose }: CustomerPortalPanelProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [translations, setTranslations] = useState<{
    title: string;
    description: string;
    buttonText: string;
    tabText: string;
    learnMore: string;
  }>({
    title: '',
    description: '',
    buttonText: '',
    tabText: '',
    learnMore: ''
  });

  useEffect(() => {
    const loadTranslations = async () => {
      const portalTranslations = await getTranslation(language, 'portal');
      setTranslations(portalTranslations);
    };
    loadTranslations();
  }, [language]);

  const handleTabClick = () => {
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    setIsExpanded(false);
  };

  const handleLearnMoreClick = () => {
    const blogSlug = language === 'de' ? 'kundenportal' : 'client-portal';
    const params = new URLSearchParams(location.search);
    const langParam = params.get('lang');
    const blogPath = `/blog/${blogSlug}${langParam ? `?lang=${langParam}` : ''}`;
    navigate(blogPath);
    setIsExpanded(false);
  };

  return (
    <div 
      className="fixed right-0 top-1/2 -translate-y-1/2 w-[368px] pointer-events-none hidden lg:block"
      style={{ zIndex: 100 }}
      onMouseLeave={handleMouseLeave}
    >
      <div 
        className={`flex transform transition-all duration-500 ease-in-out ${
          isExpanded ? 'translate-x-0' : 'translate-x-[calc(100%-48px)]'
        }`}
        style={{ willChange: 'transform' }}
      >
        <div 
          className="bg-bamboo-600 text-white px-3 py-6 rounded-l-lg shadow-lg hover:bg-bamboo-700 cursor-pointer flex flex-col items-center justify-center h-[250px] flex-shrink-0 pointer-events-auto"
          onClick={handleTabClick}
        >
          <div className="flex flex-col items-center gap-4">
            <ExternalLink 
              className={`w-5 h-5 transition-transform duration-500 ${
                isExpanded ? 'rotate-180' : 'rotate-0'
              }`}
            />
            <span className="vertical-text text-base md:text-lg font-medium tracking-wider whitespace-nowrap">{translations.tabText}</span>
          </div>
        </div>

        <div
          className="w-[320px] bg-white shadow-lg h-[250px] flex-shrink-0 pointer-events-auto"
        >
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-bamboo-800">{translations.title}</h2>
            </div>

            <p className="text-gray-600 mb-4 text-sm">
              {translations.description}
            </p>

            <button 
              onClick={handleLearnMoreClick}
              className="text-bamboo-600 hover:text-bamboo-700 flex items-center gap-1 text-sm group mb-6"
            >
              {translations.learnMore}
              <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>

            <a
              href="https://rakudoitsu-client-portal.powerappsportals.com"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-bamboo-600 text-white px-6 py-2.5 rounded-lg flex items-center justify-center gap-2 hover:bg-bamboo-700 transition-colors w-full text-sm"
            >
              {translations.buttonText}
              <ExternalLink size={18} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}